import React from 'react';
import { motion } from 'framer-motion';
import { WaitlistForm } from './WaitlistForm';
import { Activity, Heart, Zap } from 'lucide-react';
export function Hero() {
  return <section className="relative overflow-hidden bg-slate-50 pt-20 pb-32 lg:pt-32 lg:pb-40" data-id="element-50">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-51">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-full bg-teal-100/50 blur-3xl" data-id="element-52" />
        <div className="absolute top-[20%] -left-[10%] w-[40%] h-[40%] rounded-full bg-blue-100/50 blur-3xl" data-id="element-53" />
      </div>

      <div className="container mx-auto px-4 relative z-10" data-id="element-54">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20" data-id="element-55">
          {/* Text Content */}
          <motion.div className="flex-1 max-w-2xl text-center lg:text-left" initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-56">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-50 border border-teal-100 text-teal-700 text-xs font-medium mb-6" data-id="element-57">
              <span className="relative flex h-2 w-2" data-id="element-58">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" data-id="element-59"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-teal-500" data-id="element-60"></span>
              </span>
              Accepting early access
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-slate-900 mb-6 leading-[1.1]" data-id="element-61">
              Your Health, <span className="text-teal-600" data-id="element-62">Understood.</span>
            </h1>

            <p className="text-lg sm:text-xl text-slate-600 mb-8 leading-relaxed" data-id="element-63">
              Vytaniq turns wearable health data into clear, meaningful insights
              — so you know what’s normal for you and what’s changing.
            </p>

            <div className="flex flex-col items-center lg:items-start" data-id="element-64">
              <WaitlistForm data-id="element-65" />
            </div>
          </motion.div>

          {/* Visual Content */}
          <motion.div className="flex-1 w-full max-w-lg lg:max-w-none relative" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8,
          delay: 0.2
        }} data-id="element-66">
            <div className="relative aspect-square md:aspect-[4/3] lg:aspect-square" data-id="element-67">
              {/* Abstract representation of health data becoming clear */}
              <div className="absolute inset-0 bg-white rounded-3xl shadow-2xl border border-slate-100 overflow-hidden flex flex-col p-6" data-id="element-68">
                {/* Header of the mock app */}
                <div className="flex justify-between items-center mb-8" data-id="element-69">
                  <div data-id="element-70">
                    <div className="h-2 w-24 bg-slate-200 rounded mb-2" data-id="element-71"></div>
                    <div className="h-4 w-32 bg-slate-900 rounded" data-id="element-72"></div>
                  </div>
                  <div className="h-10 w-10 bg-slate-100 rounded-full" data-id="element-73"></div>
                </div>

                {/* Main Insight Card */}
                <motion.div className="bg-teal-50 rounded-2xl p-6 mb-6 border border-teal-100" initial={{
                y: 20,
                opacity: 0
              }} animate={{
                y: 0,
                opacity: 1
              }} transition={{
                delay: 0.5,
                duration: 0.5
              }} data-id="element-74">
                  <div className="flex items-start gap-4" data-id="element-75">
                    <div className="p-3 bg-teal-100 rounded-xl text-teal-600" data-id="element-76">
                      <Zap className="h-6 w-6" data-id="element-77" />
                    </div>
                    <div data-id="element-78">
                      <h3 className="font-semibold text-teal-900 mb-1" data-id="element-79">
                        Energy Baseline Improved
                      </h3>
                      <p className="text-sm text-teal-700 leading-relaxed" data-id="element-80">
                        Your resting heart rate has dropped 5% this week,
                        suggesting improved recovery and higher energy potential
                        for today.
                      </p>
                    </div>
                  </div>
                </motion.div>

                {/* Stats Grid */}
                <div className="grid grid-cols-2 gap-4" data-id="element-81">
                  <motion.div className="bg-slate-50 p-4 rounded-2xl border border-slate-100" initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.7,
                  duration: 0.5
                }} data-id="element-82">
                    <div className="flex items-center gap-2 mb-2 text-slate-500 text-sm" data-id="element-83">
                      <Heart className="h-4 w-4" data-id="element-84" /> RHR
                    </div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-85">
                      58{' '}
                      <span className="text-sm font-normal text-slate-500" data-id="element-86">
                        bpm
                      </span>
                    </div>
                    <div className="text-xs text-green-600 mt-1" data-id="element-87">
                      ↓ 2bpm from avg
                    </div>
                  </motion.div>

                  <motion.div className="bg-slate-50 p-4 rounded-2xl border border-slate-100" initial={{
                  y: 20,
                  opacity: 0
                }} animate={{
                  y: 0,
                  opacity: 1
                }} transition={{
                  delay: 0.8,
                  duration: 0.5
                }} data-id="element-88">
                    <div className="flex items-center gap-2 mb-2 text-slate-500 text-sm" data-id="element-89">
                      <Activity className="h-4 w-4" data-id="element-90" /> HRV
                    </div>
                    <div className="text-2xl font-bold text-slate-900" data-id="element-91">
                      42{' '}
                      <span className="text-sm font-normal text-slate-500" data-id="element-92">
                        ms
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1" data-id="element-93">Stable</div>
                  </motion.div>
                </div>
              </div>

              {/* Floating Elements */}
              <motion.div className="absolute -right-4 top-1/4 bg-white p-4 rounded-xl shadow-xl border border-slate-100 max-w-[200px]" animate={{
              y: [0, -10, 0]
            }} transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'easeInOut'
            }} data-id="element-94">
                <div className="flex items-center gap-3" data-id="element-95">
                  <div className="h-2 w-2 rounded-full bg-green-500" data-id="element-96"></div>
                  <p className="text-xs font-medium text-slate-700" data-id="element-97">
                    Readiness Score: 92
                  </p>
                </div>
              </motion.div>

              <motion.div className="absolute -left-4 bottom-1/4 bg-white p-4 rounded-xl shadow-xl border border-slate-100 max-w-[200px]" animate={{
              y: [0, 10, 0]
            }} transition={{
              duration: 5,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 1
            }} data-id="element-98">
                <div className="flex items-center gap-3" data-id="element-99">
                  <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600" data-id="element-100">
                    <Activity className="h-4 w-4" data-id="element-101" />
                  </div>
                  <div data-id="element-102">
                    <p className="text-xs font-medium text-slate-900" data-id="element-103">
                      Sleep Quality
                    </p>
                    <p className="text-[10px] text-slate-500" data-id="element-104">Optimal Range</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}